// types
import type { DealHistory } from '@makemydeal/dr-dash-types';
import type { FC } from 'react';

// selectors
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/trade';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';

// components
import DealSummaryItem from '../common/DealSummaryItem';
import LineItemWithChildren from '../common/LineItemWithChildren';

// constants
import * as constants from '../../constants';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

// styles
import {
    labelStyles as defaultLabelStyles,
    valueStyles as defaultValueStyles,
    detailedViewGridProps,
    detailedViewLabelStyles,
    detailedViewSecondaryGridProps,
    detailedViewValueStyles,
    secondaryValueStyles
} from '../utils/styles';

import { useDealSummary } from '../../common/DealSummaryProvider';
import { useMediaQuery } from 'react-responsive';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { Grid } from '@interstate/components/Grid';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';
import { useHeldValue } from '../../hooks/useHeldSelector';

export type AdjustedResidualProps = {
    deal?: DealHistory;
};

const DueAtSigningPaidBy: FC<AdjustedResidualProps> = ({ deal }) => {
    const useDealRootSelector = createDealRootSelector(deal, 'DueAtSigningPaidBy');
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const { showDetailedView } = useDealSummary();

    const netTradeValue = useHeldValue(
        useDealRootSelector(tradeInSelectors.getNetTradeInAllowanceValue, 'netTradeInAllowanceValue'),
        deal
    );
    const shouldShowTradeIn = netTradeValue >= 0;
    const totalRebates = useDealRootSelector(
        offerReduxSelectors.getAppliedIncentivesTotalForNonDealerCash,
        'appliedIncentivesTotalForNonDealerCash'
    );
    const getCashDueFromCustomer = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getCashDueFromCustomer, 'cashDueFromCustomer'),
        deal
    );
    const cashOffsetAmountAddedToPrice = useDealRootSelector(
        offerReduxSelectors.getCashOffsetPriorLoanBalance,
        'cashOffsetPriorLoanBalance'
    );
    const priorLoanBalance = useHeldValue(useDealRootSelector(offerReduxSelectors.getPriorLoanBalance, 'priorLoanBalance'), deal);
    const totalAmountDue = useHeldValue(
        useTrackedSelector(compositeSelectors.getTotalAmountDue, {
            component: 'DueAtSigningPaidBy',
            field: 'totalAmountDue'
        })
    );
    const isRemoveCashOffsetEnabled = useTrackedSelector(featureToggleSelectors.removeCashOffsetEnabled, {
        component: 'DueAtSigningPaidBy',
        field: 'removeCashOffsetEnabled'
    });

    const formattedTotalAmountDue = formatDollarsAndCents(totalAmountDue);

    let gridProps = {};
    if (showDetailedView && isLargeSize) {
        gridProps = {
            ...detailedViewGridProps,
            pt: '0.571rem',
            rowGap: '0.571rem'
        };
    } else {
        gridProps = detailedViewSecondaryGridProps;
    }
    const labelStyles = showDetailedView ? detailedViewLabelStyles : {};
    const valueStyles = showDetailedView ? secondaryValueStyles : {};

    return (
        <DealSummaryItem
            labelStyles={showDetailedView ? {} : defaultLabelStyles}
            valueStyles={showDetailedView ? detailedViewValueStyles : defaultValueStyles}
            label={constants.DUE_AT_SIGNING_PAID_BY}
            value={formattedTotalAmountDue}
        >
            <Grid {...gridProps}>
                {shouldShowTradeIn && (
                    <LineItemWithChildren
                        label={constants.NET_TRADE_IN_EQUITY}
                        value={formatDollarsAndCents(netTradeValue)}
                        labelStyles={labelStyles}
                        valueStyles={valueStyles}
                    />
                )}
                <LineItemWithChildren
                    label={constants.REBATES_AND_NONCASH_CREDITS}
                    value={formatDollarsAndCents(totalRebates)}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                <LineItemWithChildren
                    label={constants.AMOUNT_PAID_BY_CUSTOMER}
                    value={formatDollarsAndCents(getCashDueFromCustomer)}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                <LineItemWithChildren
                    label={constants.ADDITIONAL_CAPITALIZED_COST}
                    value={formatDollarsAndCents(isRemoveCashOffsetEnabled ? priorLoanBalance : cashOffsetAmountAddedToPrice)}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
                <LineItemWithChildren
                    label={constants.AMOUNT_PAID_BY_DEALER}
                    value={formatDollarsAndCents(0)}
                    labelStyles={labelStyles}
                    valueStyles={valueStyles}
                />
            </Grid>
        </DealSummaryItem>
    );
};

export default DueAtSigningPaidBy;
